import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import DocuSeal from "components/DocuSeal/DocuSeal";
import CustomButton from "components/common/CustomButton/CustomButton";
import { IS_BROKER_PORTAL } from "config";
import { STERE_CHECKOUT_COMPLETED } from "constants/webhookEvents";
import { fireWebhookEvent } from "store/features/webhooksSlice";
import { clientSelector, quotesSelector } from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import { sendMessageToClient } from "utils/utils";
import EsignSuccess from "./EsignSuccess";
import SuccessMessage from "./SuccessMessage";
import DocumentList from "components/common/DocumentList/DocumentList";
import { fetchSubmissionAPI, getPolicyAPI } from "api/siaApplication";
import { setAvailableQuotes, setInvalidQuotes } from "store/features/quotesSlice";
import AllianzConfirmation from "./AllianzConfirmation";

const Confirmation = () => {
  const { t } = useT();
  const dispatch = useDispatch();
  const { submissionId, siaApplicationId, updatedSiaApplication } = useSelector(
    (state: RootState) => state.surveyJs
  );
  const { selectedQuotes } = useSelector(quotesSelector);
  const { applicantId, selectedProducts, clientAPIKey } = useSelector(
    (state: RootState) => state.client
  );
  const [showDocusealSuccess, setShowDocusealSuccess] = useState(false);
  const [customMessage, setCustomMessage] = useState<string>(null);
  const { clientUrl, externalAuthConfig } = useSelector(clientSelector);
  const { autoRenewalConsent, showAutoRenewalSwitch } =
    useSelector(clientSelector);
  const isAutoRenewalActivated = showAutoRenewalSwitch || false;
  
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [pollingCompleted, setPollingCompleted] = useState(false);
  const [policyNumber, setPolicyNumber] = useState<string | number | null>(null);

  const fetchUpdatedQuotes = async () => {
    try {
      setIsLoadingDocuments(true);
      setPollingCompleted(false);
      
      // First, poll until we get a valid policyId (total: 1 minute)
      let policyId: string | undefined;
      let submissionIteration = 0;
      const maxSubmissionIterations = 30;
      const intervalMs = 2000;
      
      while (submissionIteration < maxSubmissionIterations) {
        try {
          console.log("Submission poll tick:", Date.now(), "iteration:", submissionIteration + 1);
          const response: any = await fetchSubmissionAPI(submissionId, clientAPIKey);
          
          // Check if any quote has a policy present
          const quoteWithPolicy = response.data?.quotes?.find((quote: any) => quote?.policy);
          policyId = quoteWithPolicy?.policy;
          console.log("PolicyId:", policyId);
          
          if (policyId) {
            // We have a valid policyId, break out of the loop
            break;
          }
          
          submissionIteration++;
          
          if (submissionIteration < maxSubmissionIterations) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          }
        } catch (submissionError) {
          console.error(`Error fetching submission in iteration ${submissionIteration + 1}:`, submissionError);
          submissionIteration++;
          
          if (submissionIteration < maxSubmissionIterations) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          }
        }
      }
      
      // If we still don't have a policyId after polling, exit
      if (!policyId) {
        console.error("Failed to retrieve policyId after polling");
        setIsLoadingDocuments(false);
        setPollingCompleted(true);
        return;
      }
      
      // Now poll for documents using the valid policyId
      let iteration = 0;
      const maxIterations = 10;
      
      while (iteration < maxIterations) {
        try {
          console.log("Policy poll tick:", Date.now(), "iteration:", iteration + 1);
          let policyResponse: any = await getPolicyAPI(clientAPIKey, policyId);
          console.log("Status:", policyResponse.data?.status, "| Documents:", policyResponse.data?.documents ? "present" : "null/undefined");
          
          if (policyResponse.data?.documents && policyResponse.data.documents !== null) {
            setDocuments(policyResponse.data.documents);
            setPolicyNumber(policyResponse.data?.policy_number ?? null);
            setIsLoadingDocuments(false);
            setPollingCompleted(true);
            break;
          }
          
          iteration++;
          
          if (iteration < maxIterations) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          } else {
            setIsLoadingDocuments(false);
            setPollingCompleted(true);
          }
        } catch (policyError) {
          console.error(`Error in iteration ${iteration + 1}:`, policyError);
          iteration++;
          
          if (iteration < maxIterations) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          } else {
            setIsLoadingDocuments(false);
            setPollingCompleted(true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching updated quotes:", error);
      setIsLoadingDocuments(false);
      setPollingCompleted(true);
    }
  };

  useEffect(() => {
    if ((selectedProducts.includes("DRONE-STERE") || selectedProducts.includes("AUTO-ALLIANZ")) || selectedProducts.includes("HOMEOWNERS-ALLIANZ") && submissionId) {
      fetchUpdatedQuotes();
    }
  }, [selectedProducts, submissionId]);

  useEffect(() => {
    if (!IS_BROKER_PORTAL) {
      dispatch(
        fireWebhookEvent({
          event_type: STERE_CHECKOUT_COMPLETED,
          metadata: {
            applicantId: applicantId,
            applicationId: siaApplicationId,
            submissionid: submissionId,
            selectedQuotes: selectedQuotes?.map((q) => {
              return {
                quote_id: q.id,
                premium: {
                  currency: q.premium?.currency,
                  premium_value: q.premium?.premium_value,
                  tax: q.premium?.tax,
                },
                insurer_name: q?.product_details?.insurer,
              };
            }),
            payment: {
              status: "Success",
              transactionId: sessionStorage.getItem("tId"),
            },
            policyId: sessionStorage.getItem("policyId"),
            autoRenewal: {
              available: isAutoRenewalActivated,
              ...(isAutoRenewalActivated
                ? { enabled: autoRenewalConsent }
                : {}),
            },
          },
        })
      );
      sessionStorage.removeItem("tId");
    }
  }, []);

  useEffect(() => {
    if (selectedProducts.includes("HEALTH-HBA") || selectedProducts.includes("CONDO_HOA-MOUNTAINWEST")) {
      setCustomMessage(
        "Your application is under review. Please allow us a few days for the review, and we will contact you."
      );
      return;
    }
    if (selectedProducts.includes("CTPL-PARAMOUNT")) {
      setCustomMessage(
        "Your application is submitted successfully. Please reach out to support for next steps.Thanks!"
      );
      return;
    }
    setCustomMessage(null);
  }, [selectedProducts]);

  const {clientFeatures} = useSelector(clientSelector);

  const RenderCustomMessage = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        justifyContent: "center",
        margin: "auto",
      }}
    >
      <Typography sx={{ fontSize: "1rem" }}>{customMessage}</Typography>
    </Box>
  );

  if(selectedProducts.includes("DRONE-STERE") || selectedProducts.includes("AUTO-ALLIANZ") || selectedProducts.includes("HOMEOWNERS-ALLIANZ")) {
    return (
      <AllianzConfirmation 
        documents={documents}
        isLoadingDocuments={isLoadingDocuments}
        pollingCompleted={pollingCompleted}
        policyNumber={policyNumber}
      />
    );
  }

  if (
    sessionStorage.getItem("template_slug") &&
    sessionStorage.getItem("template_slug") !== "undefined" &&
    !showDocusealSuccess
  ) {
    return (
      <DocuSeal
        email={updatedSiaApplication?.params?.contact_email}
        onComplete={() => setShowDocusealSuccess(true)}
        templateSlug={sessionStorage.getItem("template_slug")}
      />
    );
  }

  if (showDocusealSuccess) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Note - need to make it configurable */}
        <EsignSuccess brokerEmail="onceinsurance-policies@brokery.io" />
        <Box sx={{ textAlign: "right", m: "20px" }}>
          <CustomButton
            onClickCB={() => {
              sendMessageToClient({ type: "EXIT", details: {} }, clientUrl);
            }}
            btnText={t("EXIT")}
          />
        </Box>
      </Box>
    );
  }

  if (customMessage) {
    return <RenderCustomMessage />;
  }

  if(clientFeatures?.name === "LegalShield" || clientFeatures?.name === "lawpak" || clientFeatures?.name === "FleetUp") {
    return <SuccessMessage message={"Application Submitted Successfully"} />;
  }

  if(externalAuthConfig?.partner === 'fairfax') {
    return <SuccessMessage message={t("QUOTE_PAGE_SUCCESS_MESSAGE")} />;
  }

  return <SuccessMessage message={t("PAYMENT_PAGE_SUCCESS_MESSAGE")} />;
};

export default Confirmation;
